import React from 'react';
import { Star } from 'lucide-react';
import type { ResourceType } from '@cotera/api';
import { cn } from '@cotera/client/v0/lib/utils';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { useFavorites } from './use-favorites';
import { useToggleFavorite } from './use-toggle-favorite';

/**
 * The star a resource page carries in its top bar.
 *
 * Filled when starred, outlined when not — the whole state is in the glyph, so
 * the control needs no label and costs one 28px square in a bar that is already
 * crowded. The tooltip carries the verb, since an outlined star on its own does
 * not say whether clicking adds or removes.
 */
export const FavoriteToggleButton: React.FC<{
  type: ResourceType;
  id: string;
  className?: string;
  /** Glyph size, for bars that draw a smaller control than the default 28px box. */
  iconClassName?: string;
}> = ({ type, id, className, iconClassName = 'h-4 w-4' }) => {
  const { isFavorite } = useFavorites();
  const toggleFavorite = useToggleFavorite();
  const favorited = isFavorite(type, id);

  return (
    <Tooltip
      side="bottom"
      tooltipContent={favorited ? 'Unfavorite' : 'Favorite'}
    >
      <button
        type="button"
        aria-pressed={favorited}
        aria-label={favorited ? 'Unfavorite' : 'Favorite'}
        onClick={() => toggleFavorite(type, id)}
        className={cn(
          'inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md',
          'transition-colors hover:bg-secondary',
          // Starred is the state worth colouring; unstarred stays in the bar's
          // muted register so a page nobody has starred draws no attention to
          // the control.
          //
          // `ochre` — the palette's earthy amber — not Tailwind's stock
          // `amber-500`, which is a full-chroma yellow no other mark in this
          // warm, low-chroma theme reaches. Same token the Library rows use, so
          // one resource's star is the same colour wherever it is drawn.
          favorited
            ? 'text-ochre-500 hover:text-ochre-500'
            : 'text-muted-foreground hover:text-foreground',
          className
        )}
      >
        <Star
          className={iconClassName}
          // `fill-current` rather than a second colour: the fill and the stroke
          // are the same ochre, so the filled star reads as one solid mark.
          fill={favorited ? 'currentColor' : 'none'}
          aria-hidden="true"
        />
      </button>
    </Tooltip>
  );
};
